import React, { useEffect, useState } from "react";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { UsersService } from "../../../globals/services/users.service";
import { Input } from "reactstrap";
import Card from "../Card/Card";
import styles from "./index.module.scss";
import { Messages } from "./messages";

const UserListInEntity = ({ cards, entity, entityType, forceUpdate, getUsersForSession = () => {} }) => {
    const [seminarUsers, setSeminarUsers] = useState([]);
    const [seminarBackupUsers, setSeminarBackupUsers] = useState([]);
    const [usersInitials, setUsersInitials] = useState([]);
    const [filteredUsersList, setFilteredUsersList] = useState([]);
    const [regUserMsg, setRegUserMsg] = useState("");
    const [searchUserInput, setSearchUserInput] = useState("");
    const [selectedInitial, setSelectedInitial] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [loadingUserId, setLoadingUserId] = useState(null);

    useEffect(() => {
        if (cards && Array.isArray(cards)) {
            setSeminarUsers(cards);
            setSeminarBackupUsers(cards);
            setUsersInitials(groupUsersByInitials(cards));
        }
    }, [cards]);

    const groupUsersByInitials = (users) => {
        if (!users || !users.length) return [];

        const initialsSet = new Set();
        users.forEach((user) => initialsSet.add(user.fullName[0].toUpperCase()));
        return Array.from(initialsSet).sort((a, b) => (a > b ? 1 : -1));
    };

    const handleUserSearch = async (e) => {
        const { value } = e.target;
        setSearchUserInput(value);
        if (value.length > 2) {
            const filteredUsers = await UsersService().filterUserListByNameOrEmail(value);
            setFilteredUsersList(filteredUsers);
        } else {
            setRegUserMsg("");
            setFilteredUsersList([]);
        }
    };

    const handleRegisterUser = async (userId) => {
        const existingUser = seminarUsers.some(user => user._id === userId);
        if (!existingUser && !isLoading) {
            setIsLoading(true);
            setLoadingUserId(userId);
            setRegUserMsg(Messages.REGISTERING_USER);
            try {
                const response = await UsersService().registerUserToEntity(userId, entity, entityType, "participant");
                if (response) {
                    const newUser = { ...response.data, isParticipant: true, notified: false };
                    setSeminarUsers(prev => [...prev, newUser]);
                    setSeminarBackupUsers(prev => [...prev, newUser]);
                    setRegUserMsg(Messages.USER_ADDED_SUCCESS);
                    forceUpdate?.();
                }
            } catch (error) {
                setRegUserMsg(Messages.ERROR_ADDING_USER);
            } finally {
                setIsLoading(false);
                setLoadingUserId(null);
                setFilteredUsersList([]);
                setSearchUserInput("");
            }
        } else if (existingUser) {
            setFilteredUsersList([]);
            setRegUserMsg(Messages.USER_ALREADY_REGISTERED);
        }
    };

    const handleUnregisterUser = async (userid) => {
        if (!isLoading) {
            setIsLoading(true);
            setLoadingUserId(userid);
            setRegUserMsg(Messages.UNREGISTERING_USER);
            try {
                const response = await UsersService().unRegisterUserFromEntity(userid, entity, entityType, "participant");
                if (response) {
                    const updatedUsers = seminarUsers.filter((user) => user._id !== userid);
                    setSeminarUsers(updatedUsers);
                    setSeminarBackupUsers(updatedUsers);
                    setUsersInitials(groupUsersByInitials(updatedUsers));
                    setRegUserMsg(Messages.USER_REMOVED_SUCCESS);
                }
            } catch (error) {
                setRegUserMsg(Messages.ERROR_REMOVING_USER);
            } finally {
                setIsLoading(false);
                setLoadingUserId(null);
            }
        }
    };

    const handelNotifyUser = async (userId) => {
        const response = await UsersService().notifyUser(userId, entity);
        if ([200, 201, 202].includes(response.status)) {
            getUsersForSession();
        }
    };

    const getUsersByInitial = (initial) => {
        const matchingUsers = seminarBackupUsers.filter((user) => {
            const initialLetter = user.fullName[0].toUpperCase();
            return initialLetter === initial.toUpperCase();
        });
        setSeminarUsers(matchingUsers);
        setSelectedInitial(initial.toUpperCase());
    };

    const resetUsers = () => {
        setSelectedInitial("");
        setSeminarUsers(seminarBackupUsers);
    };

    return (
        <div>
            <Input
                id="selectedUserToJoinEntity"
                name="selectedUserToJoinEntity"
                placeholder="Add user to entity by name or email"
                type="text"
                onChange={handleUserSearch}
                value={searchUserInput}
                style={{ height: entityType === "class" ? "51px" : "" }}
            />

            {regUserMsg && <div className="text-danger mb-3 mt-2">{regUserMsg}</div>}

            {filteredUsersList.length > 0 && (
                <div className={"mb-3 mt-2"}>
                    {filteredUsersList.map((user) => (
                        <div key={user._id} className="d-flex flex-row add_to_entity">
                            <div className="flex-grow-1" key={user._id}>
                                {user.fullName} | {user.email}
                            </div>
                            <div className="userId">{user._id}</div>
                            <div
                                className={`buttonClass2 mr-2 ${isLoading && loadingUserId === user._id ? styles.loading : ''}`}
                                onClick={() => !isLoading && handleRegisterUser(user._id)}
                            >
                                {isLoading && loadingUserId === user._id ? (
                                    <div className="d-flex align-items-center">
                                        <div className={`${styles.spinner} me-2`}></div>
                                        <span>Adding...</span>
                                    </div>
                                ) : (
                                    'Add Participant'
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {usersInitials?.length > 0 && (
                <div className={`GroupedSessionsMenu d-flex mb-2 mt-3 gap-2 ${entityType === "class" ? "mb-3" : ""}`}>
                    <div className={`${styles.letterBtn} ${selectedInitial === "" ? styles.active : ""}`}
                        onClick={() => resetUsers()}>
                        All
                    </div>
                    {usersInitials.map((initial, i) => (
                        <div id={initial.replace(/\s/g, "")}
                            key={i}
                            className={`${styles.letterBtn} ${selectedInitial === initial ? styles.active : ""}`}
                            onClick={() => getUsersByInitial(initial)}>
                            {initial}
                        </div>
                    ))}
                </div>
            )}

            <div className="mb-2 mt-3">
                Total: <span className={styles.total}>{` ${seminarUsers.length} users`}</span>
            </div>

            <div className="d-flex flex-row flex-wrap sessionsContainer">
                {seminarUsers.map((user, index) => (
                    <Card
                        key={"user" + index}
                        user={user}
                        onClick={handleUnregisterUser}
                        isClass={entityType === "class"}
                        isUnregisterLoading={isLoading && loadingUserId === user._id}
                        handleNotifyUser={handelNotifyUser}
                    />
                ))}
            </div>
        </div>
    );
};

export default UserListInEntity;